import React, { useState } from "react";
import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import Photo from "../components/Photo";
import Reveal from "../components/Reveal";
import { Arrow } from "../components/Icons";
import { PageHead, CTABand } from "../components/Shared";
import { posts } from "../data/posts";

const fmt = (d) => new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "long", day: "numeric" });
const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];

export default function Insights() {
  const [cat, setCat] = useState("All");
  useSEO({
    title: "Insights — AI, Software Development, Cloud & Product Design Blog",
    description: "Expert articles from Victrie on artificial intelligence, custom software development, cloud architecture, mobile app costs, fintech engineering and UX design.",
    path: "/insights",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Victrie Insights",
      url: "https://victrie.com/insights",
      publisher: { "@id": "https://victrie.com/#organization" },
      blogPost: posts.map((p) => ({ "@type": "BlogPosting", headline: p.title, url: `https://victrie.com/insights/${p.slug}`, datePublished: p.date })),
    },
  });

  const list = cat === "All" ? posts : posts.filter((p) => p.category === cat);
  const [featured, ...rest] = list;

  return (
    <>
      <PageHead
        crumb="Insights"
        title={<>Ideas shaping the <span className="grad-text">next generation</span> of technology</>}
        text="Practical, honest thinking on AI, software engineering, cloud and product design from the people who build it every day."
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 28 }}>
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`btn ${cat === c ? "btn--primary" : "btn--ghost"}`} style={{ padding: "10px 18px", fontSize: "0.85rem" }}>{c}</button>
          ))}
        </div>
      </PageHead>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          {featured && (
            <Reveal className="card split" as="article" style={{ padding: 0, marginBottom: 32, gap: 0 }}>
              <Link to={`/insights/${featured.slug}`}>
                <Photo image={featured.image} alt={featured.title} hover style={{ borderRadius: 0, border: 0, height: "100%", minHeight: 320 }} />
              </Link>
              <div style={{ padding: "clamp(24px,4vw,48px)" }}>
                <div className="post-card__meta"><b>{featured.category}</b><span>{fmt(featured.date)}</span><span>{featured.readTime} min read</span></div>
                <h2 style={{ fontSize: "clamp(1.4rem,2vw,1.8rem)" }}><Link to={`/insights/${featured.slug}`}>{featured.title}</Link></h2>
                <p className="lead" style={{ fontSize: "1.05rem" }}>{featured.excerpt}</p>
                <Link to={`/insights/${featured.slug}`} className="btn btn--primary">Read article <Arrow /></Link>
              </div>
            </Reveal>
          )}
          <div className="grid grid--3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 90} className="card post-card" as="article">
                <Link to={`/insights/${p.slug}`}>
                  <Photo image={p.image} alt={p.title} hover />
                </Link>
                <div className="post-card__body">
                  <div className="post-card__meta"><b>{p.category}</b><span>{fmt(p.date)}</span></div>
                  <h3><Link to={`/insights/${p.slug}`}>{p.title}</Link></h3>
                  <p>{p.excerpt}</p>
                  <Link to={`/insights/${p.slug}`} className="link-arrow">Read article <Arrow /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title="Have a challenge worth writing about?" text="Bring it to us. The best articles start as real client problems we solved together." />
    </>
  );
}
